import React from "react";
import { Button, Typography } from "../common";

const TRIAL_URL = "https://pedagogue.io/#trial";
const isPurchaseEnabled = () =>
  process.env.REACT_APP_PURCHASE_ENABLED === "true";

interface SoftLockModalProps {
  open: boolean;
  message: string;
  onClose: () => void;
  /** If true, shows upgrade CTA alongside the close button */
  showUpgradeCTA?: boolean;
}

/**
 * SoftLockModal - Displays when user hits locked content
 *
 * Shows a message explaining why content is locked and optionally
 * provides a package CTA.
 * When PURCHASE_ENABLED=false, redirects to the guided Trial enquiry.
 * When PURCHASE_ENABLED=true, opens the App Home subscription page.
 */
const SoftLockModal: React.FC<SoftLockModalProps> = ({
  open,
  message,
  onClose,
  showUpgradeCTA = true,
}) => {
  if (!open) return null;

  const handleUpgradeClick = () => {
    onClose();
    if (isPurchaseEnabled()) {
      window.location.href = `${process.env.REACT_APP_APP_HOME_URL || "https://app.pedagogue.io"}/subscription?return_to=${encodeURIComponent(window.location.href)}`;
    } else {
      window.location.href = TRIAL_URL;
    }
  };

  return (
    <div
      data-testid="lesson-soft-lock-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-6"
    >
      <div className="max-w-xl w-full rounded-2xl bg-white shadow-xl p-8 space-y-6 text-center">
        <Typography variant="h3" weight="bold" className="text-gray-900">
          Thank you!
        </Typography>
        <Typography variant="p" className="text-gray-700 leading-7">
          {message}
        </Typography>

        {showUpgradeCTA && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <Typography variant="p" className="text-green-800 text-sm">
              Want to unlock all units and features? Upgrade your organisation
              to continue learning.
            </Typography>
          </div>
        )}

        <div className="flex justify-center gap-4">
          <Button
            variant="outline"
            size="large"
            className="px-8 py-2"
            onClick={onClose}
          >
            Got it
          </Button>
          {showUpgradeCTA && (
            <Button
              variant="filled"
              size="large"
              className="px-8 py-2 !bg-green-500 hover:!bg-green-600"
              onClick={handleUpgradeClick}
            >
              View packages
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SoftLockModal;
